import React from 'react';
import { cn } from '../../lib/cn';
import type { DashSchedule as Schedule } from '../../dashboard/types';
import { DashCard, DashFooterStrip, PercentRing, count, ringColour } from './primitives';
import { APPOINTMENT_REASONS, scheduleDayLink } from '../../dashboard/links';

const GAP_FILLER_RULES = '/settings/ai/gap-filler';
const GAP_HOVER = "Free blocks of 30 minutes or more in a provider's day. Shorter bits of free time aren't counted.";
const SUB = 'Chair time booked against what your providers can take, and the open time Gap Filler is working on.';

/**
 * The next seven days (spec 17 §5.4, rebuilt by spec 42 — chair time, not a count
 * of appointments). **`RIGHT NOW`** — unaffected by the period picker, because the
 * week ahead is the same week whichever period you are looking at.
 *
 * `hasGapFiller` false (a `?plan=core` practice, spec 37): the card shows the rings
 * and hours only — no gap counts, no chips — and the footer offers Gap Filler.
 */
export function ScheduleCard({ schedule, hasGapFiller = true }: { schedule: Schedule; hasGapFiller?: boolean }) {
  // No operatories set up is not an empty week — it is a page with nothing to
  // measure against, and it gets a link rather than a row of 0%.
  if (schedule.state === 'not_set_up') {
    return (
      <DashCard
        title="The next seven days"
        rightNow
        sub={SUB}
      >
        <div className="px-6 pb-[19px]">
          <div className="text-[13px] text-[var(--text-secondary)]">
            No operatories have been set up, so there is no capacity to measure against.
          </div>
          <a
            href={schedule.setupHref ?? '/settings/operatories'}
            className="mt-1.5 inline-block text-[12.5px] font-semibold text-[var(--brand-indigo)] hover:underline"
          >
            Not set up yet →
          </a>
        </div>
      </DashCard>
    );
  }

  // Sync behind: the whole card greys and says so, rather than showing a stale
  // week as if it were current.
  if (schedule.state === 'unknown') {
    return (
      <DashCard title="The next seven days" rightNow>
        <div className="px-6 pb-[19px] opacity-60">
          <div className="text-[13px] text-[var(--text-secondary)]">
            The schedule could not be read. Nothing here is current, so nothing is shown.
          </div>
          <div className="mt-1 text-[11.5px] text-[var(--text-muted)]">
            last good read at {schedule.lastGoodRead ?? '—'}
          </div>
        </div>
      </DashCard>
    );
  }

  const booked = schedule.days.reduce((n, d) => n + d.booked, 0);
  const capacity = schedule.days.reduce((n, d) => n + d.capacity, 0);
  const openGaps = schedule.days.reduce((n, d) => n + d.openGaps, 0);
  const pct = capacity === 0 ? 0 : Math.round((booked / capacity) * 100);

  return (
    <DashCard
      title="The next seven days"
      rightNow
      sub={SUB}
    >
      <div className="grid grid-cols-7 gap-3 px-6 pb-4 max-[1100px]:grid-cols-4">
        {schedule.days.map((d) => {
          // A closed week is real data: the ring reads `closed`, not 0%.
          const closed = d.closed === true || d.capacity === 0;
          const dayPct = closed ? undefined : Math.round((d.booked / d.capacity) * 100);
          const colour = closed ? 'var(--text-muted)' : ringColour(dayPct ?? 0);
          return (
            <a
              key={`${d.label}-${d.date}`}
              href={scheduleDayLink(d.date)}
              className="flex flex-col items-center gap-1.5 rounded-[12px] py-2 hover:bg-[#FAFBFE]"
            >
              <div className="text-[12.5px] font-bold text-[var(--text-primary)]">{d.label}</div>
              <div className="text-[11px] text-[var(--text-muted)]">{d.date}</div>
              <PercentRing
                percent={dayPct}
                label={closed ? 'closed' : String(dayPct)}
                colour={colour}
                size={closed ? 52 : 52}
              />
              <div className="font-[var(--font-mono)] text-[11.5px] text-[var(--text-secondary)]">
                {closed ? '—' : `${count(d.booked)} h of ${count(d.capacity)} h`}
              </div>
              {closed ? (
                <div className="text-[11.5px] text-[var(--text-muted)]">closed</div>
              ) : !hasGapFiller ? null : d.openGaps === 0 ? (
                <div className="text-[11.5px] font-semibold text-[var(--money-green)]" title={GAP_HOVER}>no gaps</div>
              ) : (
                <div className="text-[11.5px] font-bold text-[var(--warn-amber)]" title={GAP_HOVER}>
                  {count(d.openGaps)} {d.openGaps === 1 ? 'gap' : 'gaps'}
                </div>
              )}
            </a>
          );
        })}
      </div>

      <DashFooterStrip>
        <span className="flex flex-wrap items-center gap-x-2 gap-y-1.5">
          <span className="text-[var(--text-secondary)]">
            <b className="font-[var(--font-mono)] font-bold text-[var(--text-primary)]">
              {count(booked)} h of {count(capacity)} h
            </b>{' '}
            booked · {pct}%
          </span>
          <span className="text-[var(--text-muted)]">·</span>
          {hasGapFiller ? (
            <>
              <span className="text-[var(--text-secondary)]">
                <b className="font-[var(--font-mono)] font-bold text-[var(--warn-amber)]">{count(openGaps)}</b> open gaps —
                Gap Filler is working <b className="font-semibold text-[var(--text-primary)]">{count(schedule.gapsWorked)}</b>
              </span>
              {/* Two chips, each with its own fix. working + noReason + noRule = open gaps.
                  A chip with zero is not shown (§2). */}
              {schedule.gapsNoReason > 0 ? (
                <>
                  <span className="rounded-full bg-[#FBF3E4] px-2.5 py-1 text-[11px] font-bold text-[var(--warn-amber)]">
                    {count(schedule.gapsNoReason)} gaps no appointment type fits
                  </span>
                  <a href={APPOINTMENT_REASONS} className="text-[13px] font-semibold text-[var(--brand-indigo)] hover:underline">
                    Appointment reasons →
                  </a>
                </>
              ) : null}
              {schedule.gapsNoRule > 0 ? (
                <>
                  <span className="rounded-full bg-[#FBF3E4] px-2.5 py-1 text-[11px] font-bold text-[var(--warn-amber)]">
                    {count(schedule.gapsNoRule)} gaps no rule covers
                  </span>
                  <a href={GAP_FILLER_RULES} className="text-[13px] font-semibold text-[var(--brand-indigo)] hover:underline">
                    Gap Filler rules →
                  </a>
                </>
              ) : null}
            </>
          ) : (
            <>
              <span className="text-[var(--text-secondary)]">Gap Filler finds and fills open time</span>
              <a href="/ai-team" className="text-[13px] font-semibold text-[var(--brand-indigo)] hover:underline">
                Talk to the ELVA team
              </a>
            </>
          )}
        </span>
      </DashFooterStrip>
    </DashCard>
  );
}

/** A small helper the agent table also wants. */
export { cn };
